import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9430prh5tg6sock3a0";

type Status = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => params.append(key, String(value)));

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <div className="rounded-lg bg-background-50 border border-background-200/80 p-5 md:p-6">
      <h3 className="font-heading font-semibold text-foreground-950 mb-1">联系我们</h3>
      <p className="text-xs text-foreground-500 mb-5">
        投稿、合作或内容纠错，欢迎留下你的信息，我们会尽快回复。
      </p>

      {status === "success" && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-800">
          <i className="ri-checkbox-circle-line text-lg shrink-0"></i>
          <span>消息已发送，感谢你的留言！我们会尽快与你联系。</span>
        </div>
      )}
      {status === "error" && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md bg-secondary-100 border border-secondary-200 text-sm text-secondary-900">
          <i className="ri-error-warning-line text-lg shrink-0"></i>
          <span>{formError}</span>
        </div>
      )}

      <form data-readdy-form id="about-contact-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的姓名或昵称"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
            留言内容
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请输入你想说的话（最多 500 字）"
            className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
          ></textarea>
        </div>

        <input
          className="field-extra-guard"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
          {status === "submitting" ? "发送中..." : "发送留言"}
        </button>
      </form>
    </div>
  );
}